/**
 * The tenant a SuperAdmin is currently working inside.
 *
 * Why this exists. A SuperAdmin has no tenant of their own — their JWT
 * carries the RLS bypass sentinel. Without an explicit target, the API can
 * only answer from the platform database, so the SuperAdmin console showed
 * every shared tenant's rows merged together (each role name once per
 * tenant) and could not see a dedicated tenant's rows at all, because those
 * live in their own databases.
 *
 * The scope is ambient on purpose, exactly like `X-Tenant-Slug`: the
 * interceptor in `@/lib/api` sends it as `X-Target-Tenant-Id` on every
 * request, so a page does not have to thread a tenant id through every
 * service call and modal. A call that names its own tenant with
 * `?tenantId=` still wins server-side — explicit beats ambient.
 *
 * Only a SuperAdmin ever sets this. A tenant user's requests are scoped by
 * their JWT, and the API ignores the header for them.
 */

const STORAGE_KEY = "superadmin.tenantScope";

let currentTenantId = readStored();
const listeners = new Set();

function readStored() {
  if (typeof window === "undefined") return "";
  try {
    return window.localStorage.getItem(STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

/** The tenant id to send, or "" when there is no scope. */
export const getTenantScope = () => currentTenantId;

/**
 * Set (or clear, with "") the working tenant. Survives a reload so a
 * refresh does not silently move the SuperAdmin to another tenant's data.
 */
export const setTenantScope = (tenantId) => {
  const next = tenantId || "";
  if (next === currentTenantId) return;
  currentTenantId = next;
  try {
    if (next) window.localStorage.setItem(STORAGE_KEY, next);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private-mode storage failure is not fatal — the scope still holds
    // for this page's lifetime.
  }
  listeners.forEach((listener) => listener(next));
};

/** Subscribe to scope changes. Returns an unsubscribe function. */
export const subscribeTenantScope = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Called on logout — the next account must not inherit this scope. */
export const clearTenantScope = () => setTenantScope("");
